import { useRef, useEffect, useState } from "react";
import { YMaps, Map, Placemark, SearchControl } from "react-yandex-maps";
import { useCities } from "../providers/CitiesContext";
import { formatTransferNumber } from "../utils/formatTransferNumber";
import PhoneCheckControl from "./PhoneCheckControl";

// ===== ХЕЛПЕРЫ =====
const escapeHTML = (v) =>
  String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const text = (v) => escapeHTML(v);

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return ["true", "1", "yes", "y", "да"].includes(v.trim().toLowerCase());
  return false;
};

const isComplexActive = (complex) => toBool(complex?.status);

const YANDEX_PRESET_COLORS = [
  "darkOrange",
  "darkGreen",
  "darkBlue",
  "lightBlue",
  "night",
  "blue",
  "red",
  "green",
  "orange",
  "yellow",
  "violet",
  "pink",
  "brown",
  "black",
  "olive",
  "gray",
];

function getInactivePreset(preset) {
  if (!preset || typeof preset !== "string") {
    return "islands#grayIcon";
  }

  const prefix = "islands#";

  if (!preset.startsWith(prefix)) {
    return "islands#grayIcon";
  }

  const body = preset.slice(prefix.length);
  const color = YANDEX_PRESET_COLORS.find((item) => body.startsWith(item));

  if (!color) {
    return "islands#grayIcon";
  }

  const iconType = body.slice(color.length);

  if (!iconType) {
    return "islands#grayIcon";
  }

  return `${prefix}gray${iconType}`;
}

const isNonEmptyObject = (o) => o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;

const normArray = (a) => (Array.isArray(a) ? a.map((x) => String(x).trim()).filter(Boolean) : []);

const joinArray = (a, sep = ", ") => normArray(a).join(sep);

// Примитивная санитаризация "описания", если оно с HTML:
// - вырезаем <script/style/iframe> целиком
// - убираем on* атрибуты (onload, onclick, ...)
// Если хочешь абсолютно всё экранировать — просто верни escapeHTML(info)
const sanitizeInfoHTML = (html) => {
  if (typeof html !== "string") return "";
  let s = html.replace(/<(script|style|iframe)\b[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+\s*=\s*(['"]).*?\1/gi, ""); // удаляем inline-обработчики
  return s;
};

// Безопасный блок ЦЕН
const buildPricesHTML = (prices) => {
  if (!isNonEmptyObject(prices)) {
    return `<p><strong>Цены:</strong> нет данных</p>`;
  }

  try {
    const blocks = Object.entries(prices).map(([key, list]) => {
      const items = Array.isArray(list) ? list : [];
      const lis = items.map((p) => {
        const type = text(p?.type ?? "—");
        const area = text(p?.area_from_m2 ?? "—");
        const price = text(p?.price_from_mln ?? "—");
        return `<li>${type} — от ${area} м² — от ${price} млн руб.</li>`;
      }).join("");

      return `
        <div>
          <strong>${text(key)}</strong>
          <ul>${lis || "<li>нет позиций</li>"}</ul>
        </div>
      `;
    }).join("");

    return blocks
      ? `<p><strong>Цены:</strong></p>${blocks}`
      : `<p><strong>Цены:</strong> нет данных</p>`;
  } catch {
    return `<p><strong>Цены:</strong> нет данных</p>`;
  }
};

// ===== ОСНОВНАЯ ФУНКЦИЯ =====
function buildBalloonHTML(complex) {
  const isActive = !complex || typeof complex !== "object" ? true : isComplexActive(complex);
  const statusBadge = isActive ? "" : `<span class="balloon-status-badge">Стоп</span>`;
  const wrapStart = `<div class="balloon-card${isActive ? "" : " balloon-card--inactive"}">`;
  const wrapEnd = `</div>`;

  // Полный "план Б", если пришла совсем дичь
  if (!complex || typeof complex !== "object") {
    return `
      ${wrapStart}
        <h2>Объект</h2>
        <p>Данные временно недоступны.</p>
      ${wrapEnd}
    `;
  }

  try {
    const name = text(complex.name ?? "Без названия");
    const type = text(complex.type ?? "");
    const transfer = text(formatTransferNumber(complex.transfer_number));
    const developer = text(complex.developer ?? "");
    const location = text(complex.location ?? "");
    const metro = text(complex.metro ?? "");
    const finishes = joinArray(complex.finishes_available);
    const working = text(complex.working_hours ?? "");

    const completion = joinArray(complex.completion_dates);
    const pricesHTML = buildPricesHTML(complex.prices);
    const isApt = toBool(complex?.isApartments)
      ? `<p><strong>Статус помещений:</strong> Апартаменты</p>`
      : "";

    // SPECIAL: отдаём html-инфо (чуть санитизируем)
    if (complex.category === "special" || complex.category === "vehicles") {
      const infoHTML = sanitizeInfoHTML(complex.info);
      return `
        ${wrapStart}
          <h2>${name}</h2>
          ${statusBadge}
          <div>${infoHTML}</div>
        ${wrapEnd}
      `;
    }

    // COMMERCIAL: тоже поддержим info с HTML
    if (complex.category === "commercial") {
      const infoHTML = sanitizeInfoHTML(complex.info);
      return `
        ${wrapStart}
          <h2>${name}</h2>
          ${statusBadge}
          ${type ? `<p><strong>Тип перевода:</strong> ${type}</p>` : ""}
          ${transfer ? `<p><strong>Номер перевода:</strong> ${transfer}</p>` : ""}
          ${completion ? `<p><strong>Срок сдачи:</strong> ${completion}</p>` : ""}
          ${developer ? `<p><strong>Застройщик:</strong> ${developer}</p>` : ""}
          ${location ? `<p><strong>Расположение:</strong> ${location}</p>` : ""}
          ${infoHTML ? `<div>${infoHTML}</div>` : ""}
          ${working ? `<p><strong>Время работы:</strong> ${working}</p>` : ""}
        ${wrapEnd}
      `;
    }

    const typeHTML = sanitizeInfoHTML(complex.type);
    // RESIDENTIAL / default
    return `
      ${wrapStart}
        <h2>${name}</h2>
        ${statusBadge}
        ${type ? `<p><strong>Тип перевода:</strong> <span>${typeHTML}</span>` : ""}
        ${transfer ? `<p><strong>Номер перевода:</strong> ${transfer}</p>` : ""}
        ${completion ? `<p><strong>Срок сдачи:</strong> ${completion}</p>` : ""}
        ${developer ? `<p><strong>Застройщик:</strong> ${developer}</p>` : ""}
        ${location ? `<p><strong>Расположение:</strong> ${location}</p>` : ""}
        ${metro ? `<p><strong>Метро:</strong> ${metro}</p>` : ""}
        ${isApt}
        ${pricesHTML}
        ${finishes ? `<p><strong>Отделка:</strong> ${text(finishes)}</p>` : `<p><strong>Отделка:</strong> —</p>`}
        ${working ? `<p><strong>Время работы:</strong> ${working}</p>` : ""}
      ${wrapEnd}
    `;
  } catch (err) {
    // ни при каких условиях не валим приложение
    console.warn("⚠️ Ошибка buildBalloonHTML:", err, { complex });
    return `
      ${wrapStart}
        <h2>${text(complex?.name ?? "Объект")}</h2>
        <p>Не удалось отобразить детали. Попробуйте позже.</p>
      ${wrapEnd}
    `;
  }
}




function MapSection({
  complexes,
  sidebarOpen,
  filters,
  setSelectedComplexId,
  selectedCity,
  setSelectedCity,
}) {
  const { cityByKey, isCitiesLoading, citiesError } = useCities();
  const currentCityMeta = cityByKey[selectedCity];

  const mapRef = useRef(null);
  const [ymapsInstance, setYmapsInstance] = useState(null);

  useEffect(() => {
    if (mapRef.current && ymapsInstance) {
      // небольшая задержка, чтобы DOM успел обновиться
      setTimeout(() => {
        mapRef.current.container.fitToViewport();
      }, 0);
    }
  }, [sidebarOpen, ymapsInstance]);

  const handleMapLoad = (ymaps) => {
    setYmapsInstance(ymaps);
    // ❌ никаких настроек scrollZoom — всё по умолчанию
  };

  // Переключение городов
  useEffect(() => {
    if (mapRef.current && currentCityMeta) {
      mapRef.current.setCenter(currentCityMeta.center, currentCityMeta.zoom, { duration: 500 });
    }
  }, [selectedCity, currentCityMeta]);

  const className = `map ${sidebarOpen ? "with-sidebar" : "full"}`;

  if (!currentCityMeta) {
    return <div className={className}><div className="map-loading-state">{isCitiesLoading ? "Загрузка карты…" : citiesError || "Город не выбран"}</div></div>;
  }

  return (
    <div className={className}>
      <YMaps query={{ apikey: "806e5f07-631f-4f6a-828e-de27c3fde632", lang: "ru_RU" }}>
        <Map
          onLoad={handleMapLoad}
          defaultState={{ center: currentCityMeta.center, zoom: currentCityMeta.zoom, controls: [] }}
          modules={["behavior.ScrollZoom", "control.SearchControl", "control.RulerControl", "geoObject.addon.balloon"]}
          instanceRef={(ref) => {
            if (ref) mapRef.current = ref;
             try {
                // Вариант 1: коротко по алиасу
                ref.controls.add("rulerControl", { float: "right" });
                } catch (e) {
                console.warn("Не удалось добавить RulerControl:", e);
              }
          }}
          options={{
            // вернули дефолты; если нужно — оставь только тип карты
            yandexMapType: "future_map",
          }}
          width="100%"
          height="100%"
        >
          <SearchControl options={{ noPlacemark: true, float: "left" }} />
          {complexes.map((c, i) => {
            const markerPreset = isComplexActive(c)
              ? c.preset || "islands#blueIcon"
              : getInactivePreset(c.preset);

            return (
              <Placemark
                key={c._id || i}
                geometry={c.coordinates}
                modules={["geoObject.addon.balloon"]}
                properties={{
                  // можно разнести на header/body, но body тоже ок
                  balloonContentBody: buildBalloonHTML(c),
                  iconCaption: c.name,
                }}
                options={{
                  preset: markerPreset,
                  iconCaptionMaxWidth: "170",
                  hideIconOnBalloonOpen: false,
                  balloonCloseButton: true,
                  balloonAutoPan: true,          // автопрокрутка/сдвиг карты к балуну
                  balloonPanelMaxMapArea: 0,     // не уводить в «панель» на маленьких экранах
                }}
                onClick={(e) => {
                  setSelectedComplexId?.(c._id);
                  const pm = e.get("target");
                  // гарантированно открываем балун при клике:
                  if (pm && pm.balloon && !pm.balloon.isOpen()) pm.balloon.open();
                }}            />
            );
          })}
        </Map>
      </YMaps>
      <PhoneCheckControl />
    </div>
  );
}

export default MapSection;
